import { useSyncExternalStore } from "react";

/** A company link tapped on a phone, whose company is still being read. */
export type CompanyLinkOpening = {
  /** Which tap: only the latest one may open the panel. */
  readonly tap: number;
  /** The link's own label, shown while the company is read. */
  readonly label: string;
};

let opening: CompanyLinkOpening | null = null;
let lastTap = 0;
const listeners = new Set<() => void>();

const emit = (next: CompanyLinkOpening | null) => {
  opening = next;
  listeners.forEach((listener) => listener());
};

/**
 * The company a tapped link is opening, between the tap and the read that
 * answers it: the panel shows it at once, by name, so the tap is never dead
 * on a slow network. Module state, as the panel's own store is: the link and
 * the panel are far apart in the tree.
 */
export const companyLinkOpeningStore = {
  getSnapshot: (): CompanyLinkOpening | null => opening,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** A new tap: it supersedes any earlier one. Returns its number. */
  start(label: string): number {
    lastTap += 1;
    emit({ tap: lastTap, label });
    return lastTap;
  },
  /** Whether `tap` is still the one the user is waiting on. */
  isCurrent: (tap: number): boolean => opening?.tap === tap,
  /** The read for `tap` answered: the panel takes over, or an error says so. */
  finish(tap: number): void {
    if (opening?.tap === tap) emit(null);
  },
  /** The user closed the panel before the company came: drop the read. */
  cancel(): void {
    if (opening) emit(null);
  },
};

/** The link being opened, for the panel. */
export function useCompanyLinkOpening(): CompanyLinkOpening | null {
  return useSyncExternalStore(
    companyLinkOpeningStore.subscribe,
    companyLinkOpeningStore.getSnapshot,
    () => null,
  );
}
